import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MapPin, Phone, Save, Trash2, Ruler } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, isLeader, roleLabel } from "@/lib/api";
import { freeDaysBetween } from "@/lib/holidays";
import { useAuth } from "@/context/AuthContext";
import LineItems from "@/components/LineItems";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const L = ({ children }) => (
  <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{children}</Label>
);

const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : "");
// Tider sparas som lokal tid utan tidszon – ingen UTC-konvertering, ingen förskjutning.
const toStored = (v) => (v ? `${v.slice(0, 16)}:00` : null);
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const TYPE_STYLE = {
  field: { active: "bg-green-600 text-white hover:bg-green-700", idle: "border-green-300 bg-white text-green-800 hover:bg-green-50" },
  workshop: { active: "bg-yellow-400 text-slate-900 hover:bg-yellow-500", idle: "border-yellow-300 bg-white text-yellow-800 hover:bg-yellow-50" },
  open: { active: "bg-blue-600 text-white hover:bg-blue-700", idle: "border-blue-300 bg-white text-blue-800 hover:bg-blue-50" },
  installation: { active: "bg-purple-700 text-white hover:bg-purple-800", idle: "border-purple-300 bg-white font-semibold text-purple-800 hover:bg-purple-50" },
};

export const JobForm = ({ job, staff, onSaved, onClose, initialDate, initialUser }) => {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const leader = isLeader(user?.role);
  const hasInstallation = !!company?.features?.nymontering;
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [distance, setDistance] = useState({ km: null, note: null, loading: false });
  const [avail, setAvail] = useState({});
  const [conflictNames, setConflictNames] = useState([]);
  const [freeDays, setFreeDays] = useState([]);
  const [freeChoice, setFreeChoice] = useState(null);

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setForm({
      title: job?.title || "",
      job_type: job?.job_type || "field",
      machine_no: job?.machine_no || "",
      customer_id: job?.customer_id || "",
      customer_name: job?.customer_name || "",
      customer_address: job?.customer_address || "",
      customer_phone: job?.customer_phone || "",
      address: job?.address || "",
      description: job?.description || "",
      note: "",
      materials: job?.materials || [],
      spare_parts: job?.spare_parts || [],
      others: job?.others || [],
      start: toLocalInput(job?.start) || `${initialDate || todayLocal()}T08:00`,
      end: toLocalInput(job?.end),
      assigned_to: job?.assigned_to || (initialUser ? [initialUser] : []),
    });
    setDistance({ km: job?.distance_km ?? null, note: job?.distance_note ?? null, loading: false });
  }, [job, initialDate, initialUser]);

  // Kontrollera dubbelbokning för valt datum/tid
  useEffect(() => {
    if (!leader || !form.start) return;
    let ignore = false;
    const params = { start: toStored(form.start) };
    if (form.end) params.end = toStored(form.end);
    if (job?.id) params.exclude_job_id = job.id;
    api
      .get("/availability", { params })
      .then(({ data }) => {
        if (ignore) return;
        setAvail(Object.fromEntries((data.users || []).map((u) => [u.user_id, u])));
      })
      .catch(() => {});
    return () => { ignore = true; };
  }, [form.start, form.end, leader, job?.id]);

  const set = (k, v) => {
    if (k === "start" || k === "end") setFreeChoice(null);
    setForm((f) => ({ ...f, [k]: v }));
  };

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      customer_id: id,
      customer_name: c?.name || f.customer_name,
      customer_address: c?.address || "",
      customer_phone: c?.phone || "",
      address: f.address || c?.address || "",
    }));
  };

  const toggleTech = (id) =>
    setForm((f) => ({
      ...f,
      assigned_to: f.assigned_to.includes(id)
        ? f.assigned_to.filter((x) => x !== id)
        : [...f.assigned_to, id],
    }));

  const calcDistance = async () => {
    if (!form.address) return;
    setDistance((d) => ({ ...d, loading: true }));
    try {
      const { data } = await api.post("/geo/distance", { address: form.address });
      setDistance({ km: data.distance_km, note: data.distance_note, loading: false });
    } catch (e) {
      setDistance({ km: null, note: apiError(e), loading: false });
    }
  };

  const canEditAddress = leader; // admin/chef/planerare(arbetsledare)
  const installation = form.job_type === "installation";
  const workshop = form.job_type === "workshop" || installation;
  const rental = form.job_type === "open";

  const save = async () => {
    if (!form.title?.trim()) {
      toast.error(t("jobForm.titleRequired"));
      return;
    }
    const busyNames = (form.assigned_to || [])
      .filter((id) => avail[id] && !avail[id].available)
      .map((id) => staff?.find((s) => s.id === id)?.name || "Tekniker");
    const free = freeDaysBetween(toStored(form.start), toStored(form.end));
    if (free.length > 0 && freeChoice === null) {
      setFreeDays(free);
      return;
    }
    if (busyNames.length > 0) {
      setConflictNames(busyNames);
      return;
    }
    await persist(false);
  };

  const persist = async (force, choice = freeChoice) => {
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        job_type: form.job_type,
        machine_no: installation ? form.machine_no : null,
        customer_id: form.customer_id || null,
        customer_name: form.customer_name,
        customer_address: form.customer_address,
        customer_phone: form.customer_phone,
        address: workshop ? null : form.address,
        description: form.description,
        materials: form.materials,
        spare_parts: form.spare_parts,
        others: form.others,
        start: toStored(form.start),
        end: toStored(form.end),
        assigned_to: form.assigned_to,
        force: !!force,
        include_free_days: choice !== false,
      };
      if (job?.id) {
        await api.put(`/jobs/${job.id}`, payload);
        if (form.note?.trim()) await api.post(`/jobs/${job.id}/notes`, { text: form.note.trim(), phase: "during" });
      } else {
        const { data } = await api.post("/jobs", { ...payload, note: form.note?.trim() || null });
        toast.success(t("jobForm.created", { number: String(data.order_number).padStart(4, "0") }));
        if (installation) {
          onClose?.();
          onSaved?.();
          navigate(`/nymontering/${data.id}`);
          return;
        }
      }
      if (job?.id) toast.success(t("jobForm.updated"));
      setConflictNames([]);
      onClose?.();
      onSaved?.();
    } catch (e) {
      if (e?.response?.status === 409) {
        setConflictNames(["Tekniker"]);
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Ta bort arbetsordern permanent?")) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Arbetsorder borttagen");
      onClose?.();
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <L>{t("jobForm.heading")}</L>
        <Input
          data-testid="job-title-input"
          disabled={!leader}
          value={form.title || ""}
          onChange={(e) => set("title", e.target.value)}
          className="mt-1 h-10"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          ["field", t("jobForm.typeField")],
          ["workshop", t("jobForm.typeWorkshop")],
          ["open", t("jobForm.typeOpen")],
          ...(hasInstallation ? [["installation", "nymontering"]] : []),
        ].map(([v, label]) => (
          <Button
            key={v}
            type="button"
            data-testid={`job-type-${v}`}
            disabled={!leader}
            onClick={() => set("job_type", v)}
            variant={form.job_type === v ? "default" : "outline"}
            className={`h-10 flex-1 transition-colors duration-200 ${
              form.job_type === v
                ? TYPE_STYLE[v].active
                : TYPE_STYLE[v].idle
            }`}
          >
            {label}
          </Button>
        ))}
      </div>

      {installation && (
        <div className="rounded-md border border-purple-200 bg-purple-50/70 p-3" data-testid="job-installation-block">
          <p className="text-sm font-semibold text-purple-900">Nymontering – verkstads-/monteringsarbete</p>
          <p className="mt-0.5 text-xs text-purple-800">
            Ingen adress, karta, navigation eller körsträcka. Tid registreras med START/PAUS/STOP och avslutas med AVSLUT.
          </p>
          <div className="mt-2">
            <L>Maskinnr</L>
            <Input
              data-testid="job-machine-no-input"
              disabled={!leader}
              value={form.machine_no || ""}
              onChange={(e) => set("machine_no", e.target.value)}
              className="mt-1 h-10"
            />
          </div>
        </div>
      )}

      {!rental && (
      <div>
        <L>{t("jobForm.customerFromSystem")}</L>
        <Select value={form.customer_id || ""} onValueChange={pickCustomer} disabled={!leader}>
          <SelectTrigger data-testid="job-customer-select" className="mt-1 h-10">
            <SelectValue placeholder={t("jobForm.chooseCustomer")} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {customers.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`job-customer-option-${c.id}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid="job-customer-input"
          disabled={!leader}
          placeholder={t("jobForm.orTypeCustomer")}
          value={form.customer_name || ""}
          onChange={(e) => set("customer_name", e.target.value)}
          className="mt-2 h-10"
        />
      </div>
      )}

      {!installation && !rental && (
      <div>
        <L>{t("jobForm.customerAddress")}</L>
        <Input
          data-testid="job-customer-address-input"
          disabled={!leader}
          value={form.customer_address || ""}
          onChange={(e) => set("customer_address", e.target.value)}
          className="mt-1 h-10"
        />
      </div>
      )}

      {!rental && (
      <div>
        <L>{t("jobForm.customerPhone")}</L>
        <div className="mt-1 flex gap-2">
          <Input
            data-testid="job-customer-phone-input"
            disabled={!leader}
            value={form.customer_phone || ""}
            onChange={(e) => set("customer_phone", e.target.value)}
            className="h-10"
          />
          <a
            data-testid="job-call-customer"
            href={`tel:${(form.customer_phone || "").replace(/\s/g, "")}`}
            className={`inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-sm font-medium text-white transition-colors duration-200 ${
              form.customer_phone ? "bg-green-600 hover:bg-green-700" : "pointer-events-none bg-slate-300"
            }`}
          >
            <Phone className="h-4 w-4" /> {t("jobForm.call")}
          </a>
        </div>
      </div>
      )}

      {!installation && !rental && (
      <div>
        <L>{t("jobForm.siteAddress")}</L>
        <Input
          data-testid="job-address-input"
          disabled={workshop || !canEditAddress}
          placeholder={workshop ? t("jobForm.workshopNoAddress") : form.job_type === "open" ? t("jobForm.optionalAddress") : t("jobForm.addressPlaceholder")}
          value={workshop ? "" : form.address || ""}
          onChange={(e) => set("address", e.target.value)}
          className="mt-1 h-10"
        />
        {!workshop && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="job-distance-button"
              onClick={calcDistance}
              disabled={!form.address || distance.loading}
              className="h-9 bg-white transition-colors duration-200 hover:bg-slate-100"
            >
              {distance.loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Ruler className="mr-1.5 h-3.5 w-3.5" />}
              {t("jobForm.calcDistance")}
            </Button>
            <span className="text-sm text-slate-600" data-testid="job-distance-value">
              {distance.km != null
                ? t("jobForm.distanceResult", { single: (distance.km / 10).toFixed(1), total: (distance.km / 5).toFixed(1) })
                : distance.note || "–"}
            </span>
          </div>
        )}
        {!canEditAddress && !workshop && (
          <p className="mt-1 text-xs text-slate-500">{t("jobForm.addressReadOnly")}</p>
        )}
      </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <L>{t("common.start")}</L>
          <Input
            type="datetime-local"
            data-testid="job-start-input"
            disabled={!leader}
            value={form.start || ""}
            onChange={(e) => set("start", e.target.value)}
            className="mt-1 h-10"
          />
        </div>
        <div>
          <L>{t("common.end")}</L>
          <Input
            type="datetime-local"
            data-testid="job-end-input"
            disabled={!leader}
            value={form.end || ""}
            onChange={(e) => set("end", e.target.value)}
            className="mt-1 h-10"
          />
        </div>
      </div>

      {leader && (
        <div>
          <L>{rental ? "Tekniker / chaufför" : t("jobForm.assigned")}</L>
          <div
            data-testid="job-assignee-list"
            className="mt-1 max-h-44 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2"
          >
            {staff?.map((s) => {
              const a = avail[s.id];
              const selectedAlready = form.assigned_to?.includes(s.id) || false;
              const isBusy = !!a && !a.available;
              const busyText = a?.absence
                ? `${t("jobForm.absentLabel")}: ${t(`absType.${a.absence.type}`)}`
                : (a?.conflicts || [])
                    .map((c) => `#${c.order_number || "?"} ${String(c.start || "").slice(11, 16)}${c.end ? `–${String(c.end).slice(11, 16)}` : ""}`)
                    .join(", ");
              return (
                <label
                  key={s.id}
                  htmlFor={`tech-${s.id}`}
                  data-testid={`job-assignee-row-${s.id}`}
                  className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-2 transition-colors duration-200 hover:bg-slate-50"
                >
                  <Checkbox
                    id={`tech-${s.id}`}
                    data-testid={`job-assignee-option-${s.id}`}
                    checked={selectedAlready}
                    onCheckedChange={() => toggleTech(s.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800">{s.name}</span>
                    {isBusy && (
                      <span
                        className="block truncate text-xs font-semibold text-red-600"
                        data-testid={`job-assignee-busy-${s.id}`}
                      >
                        Upptagen{busyText ? `: ${busyText}` : ""}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-slate-500">{roleLabel(s.role, t)}</span>
                </label>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-slate-500" data-testid="job-assignee-count">
            {form.assigned_to?.length || 0} {t("common.selected")}
          </p>
        </div>
      )}

      {!rental && (
      <div>
        <L>{t("jobForm.description")}</L>
        <Textarea
          data-testid="job-description-input"
          value={form.description || ""}
          onChange={(e) => set("description", e.target.value)}
          className="mt-1"
          rows={2}
        />
      </div>
      )}

      <div>
        <L>{t("jobForm.freeText")}</L>
        <Textarea
          data-testid="job-note-input"
          placeholder={t("jobForm.freeTextPlaceholder")}
          value={form.note || ""}
          onChange={(e) => set("note", e.target.value)}
          className="mt-1"
          rows={2}
        />
      </div>

      {!rental && (
      <>
      <LineItems
        label="Material"
        testid="job-materials"
        category="material"
        items={form.materials}
        onChange={(v) => set("materials", v)}
      />
      <LineItems
        label="Reservdelar"
        testid="job-spare-parts"
        category="spare_part"
        items={form.spare_parts}
        onChange={(v) => set("spare_parts", v)}
      />
      <LineItems
        label="Annat"
        testid="job-others"
        category="other"
        items={form.others}
        onChange={(v) => set("others", v)}
      />
      </>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        {job?.id && leader ? (
          <Button
            variant="outline"
            data-testid="job-delete-button"
            onClick={remove}
            className="border-red-200 text-red-600 transition-colors duration-200 hover:bg-red-50"
          >
            <Trash2 className="mr-2 h-4 w-4" /> {t("common.delete")}
          </Button>
        ) : (
          <span />
        )}
        <Button
          data-testid="job-save-button"
          onClick={save}
          disabled={busy}
          className="bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> {t("common.save")}</>}
        </Button>
      </div>

      <AlertDialog open={conflictNames.length > 0} onOpenChange={(o) => !o && setConflictNames([])}>
        <AlertDialogContent className="bg-white" data-testid="job-conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="job-conflict-title">
              {conflictNames.length > 1
                ? `${conflictNames.length} av de valda teknikerna är redan bokade under hela eller delar av denna tid. Vill du använda dem ändå?`
                : "Teknikern är redan bokad under hela eller delar av denna tid. Vill du använda teknikern ändå?"}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="job-conflict-names">
              {conflictNames.join(", ")} – den befintliga bokningen påverkas inte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="job-conflict-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="job-conflict-ok"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); persist(true); }}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Använd ändå
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={freeDays.length > 0} onOpenChange={(o) => !o && setFreeDays([])}>
        <AlertDialogContent className="bg-white" data-testid="job-weekend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Bokningen sträcker sig över helg eller röd dag. Vill du boka jobbet även dessa dagar?
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="job-weekend-days">
              {freeDays.map((d) => `${d.date} (${d.name})`).join(", ")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              data-testid="job-weekend-skip"
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                setFreeChoice(false);
                setFreeDays([]);
                persist(false, false);
              }}
              className="bg-white text-slate-800 ring-1 ring-slate-200 hover:bg-slate-100"
            >
              Nej, hoppa över dem
            </AlertDialogAction>
            <AlertDialogAction
              data-testid="job-weekend-include"
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                setFreeChoice(true);
                setFreeDays([]);
                persist(false, true);
              }}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Ja, boka även helgen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobForm;
